import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Anchor, CheckCircle2, Circle, Clock, AlertCircle, ArrowRight,
  Loader2, RefreshCw, Search,
} from "lucide-react";
import { HarborWellFormHeader } from "@/components/HarborWellFormHeader";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  submitted: { label: "Received", tone: "info" },
  in_review: { label: "Under Review", tone: "info" },
  info_requested: { label: "Additional Info Requested", tone: "warn" },
  approved: { label: "Approved", tone: "success" },
  rejected: { label: "Declined", tone: "error" },
  archived: { label: "Archived", tone: "muted" },
};

const TONE_COLORS = {
  info:    { bg: "#DBEAFE", fg: "#1E3A8A" },
  warn:    { bg: "#FEF3C7", fg: "#92400E" },
  success: { bg: "#DCFCE7", fg: "#166534" },
  error:   { bg: "#FEE2E2", fg: "#991B1B" },
  muted:   { bg: "#E5E5E0", fg: "#64748B" },
};

function TimelineStep({ step, isLast }) {
  const status = step.status;
  const Icon = status === "complete"
    ? CheckCircle2
    : status === "current"
      ? Clock
      : Circle;
  const skipped = status === "not_applicable";
  const color = status === "complete"
    ? "#166534"
    : status === "current"
      ? "#1E3A8A"
      : skipped ? "#CBD5E1" : "#94A3B8";
  const bg = status === "complete"
    ? "#DCFCE7"
    : status === "current"
      ? "#DBEAFE"
      : skipped ? "transparent" : "#F1F5F9";
  return (
    <div className="flex items-start gap-3" data-testid={`timeline-step-${step.key}`}>
      <div className="flex flex-col items-center" style={{ minWidth: 32 }}>
        <div style={{
          width: 32, height: 32, borderRadius: "50%", background: bg,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        {!isLast && (
          <div style={{
            width: 2, flex: 1, minHeight: 32,
            background: status === "complete" ? "#166534" : "#E5E5E0",
            marginTop: 4,
          }} />
        )}
      </div>
      <div style={{ paddingBottom: isLast ? 0 : "1rem", flex: 1 }}>
        <div className="text-sm font-medium"
              style={{ color: status === "current" ? "#1E3A8A" : "#0F172A",
                        textDecoration: skipped ? "line-through" : "none",
                        opacity: skipped ? 0.5 : 1 }}
              data-testid={`timeline-step-label-${step.key}`}>
          {step.label}
        </div>
        <div className="text-xs" style={{ color: "#64748B", marginTop: 2 }}
              data-testid={`timeline-step-status-${step.key}`}>
          {status === "complete" && "Completed"}
          {status === "current" && "In progress"}
          {status === "pending" && "Pending"}
          {status === "not_applicable" && "Not required"}
        </div>
      </div>
    </div>
  );
}

export default function PartnerApplicationStatus() {
  const { publicRef: paramRef } = useParams();
  const [search] = useSearchParams();

  const [refInput, setRefInput] = useState(paramRef || search.get("ref") || "");
  const [ref, setRef] = useState(paramRef || search.get("ref") || "");
  const [status, setStatus] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    setErr("");
    setNotFound(false);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/public/partner-applications/status/${encodeURIComponent(id)}`,
      );
      if (!data.found) {
        setNotFound(true);
        setStatus(null);
      } else {
        setStatus(data);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Lookup failed.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (ref) load(ref);
  }, [ref, load]);

  const submitLookup = (e) => {
    e.preventDefault();
    setRef(refInput.trim());
  };

  const meta = status ? (STATUS_META[status.status] || { label: status.status, tone: "muted" }) : null;
  const toneColors = meta ? TONE_COLORS[meta.tone] : null;

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <header className="bg-white border-b border-[#E5E5E0] py-4 px-6">
        <div className="max-w-6xl mx-auto">
          <HarborWellFormHeader
            eyebrow="HarborWell Connect"
            title="Application Status"
            size="md"
            testId="partner-status-brand-header"
            rightSlot={
              <Link to="/partner/apply-integrations" className="text-sm text-[#1E3A8A] hover:underline"
                    data-testid="link-new-application">
                New application <ArrowRight className="w-3.5 h-3.5 inline" />
              </Link>
            }
          />
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-6" data-testid="partner-status-page">
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-5 mb-6">
          <h1 className="font-display text-2xl font-semibold text-[#1E3A8A] mb-2">
            Track your partner application
          </h1>
          <p className="text-sm text-slate-600 mb-4">
            Enter the reference number from your confirmation email
            (looks like <code className="font-mono">HW-APP-XXXXXXXX</code>).
          </p>
          <form onSubmit={submitLookup} className="flex gap-2">
            <input value={refInput} onChange={(e) => setRefInput(e.target.value)}
                    className="flex-1 px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30"
                    placeholder="HW-APP-XXXXXXXX"
                    data-testid="status-ref-input" />
            <button type="submit" disabled={!refInput.trim() || loading}
              data-testid="status-lookup-btn"
              className="inline-flex items-center gap-2 bg-[#1E3A8A] text-white px-5 py-2 rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50">
              {loading
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Search className="w-4 h-4" />}
              Look up
            </button>
          </form>
        </div>

        {err && (
          <div className="bg-[#FEE2E2] border border-[#FCA5A5] rounded-xl p-4 text-sm text-[#991B1B] flex items-start gap-2 mb-4"
                data-testid="status-error">
            <AlertCircle className="w-4 h-4 mt-0.5" />
            <span>{err}</span>
          </div>
        )}

        {notFound && (
          <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6 text-center"
                data-testid="status-not-found">
            <AlertCircle className="w-8 h-8 mx-auto text-[#F59E0B] mb-2" />
            <p className="text-sm text-slate-700">
              We couldn&apos;t find an application with that reference number.
              Please double-check the code from your confirmation email.
            </p>
          </div>
        )}

        {status && (
          <div className="space-y-4" data-testid="status-detail">
            <div className="bg-white border border-[#E5E5E0] rounded-2xl p-5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">
                    Application
                  </div>
                  <div className="font-display text-xl font-semibold text-[#1E3A8A]"
                        data-testid="status-org-name">
                    {status.organization_name}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Ref <code className="font-mono" data-testid="status-public-ref">{status.public_ref}</code>
                    {" · Submitted "}
                    {new Date(status.submitted_at).toLocaleDateString()}
                  </div>
                </div>
                {meta && (
                  <span data-testid="status-badge"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      padding: "6px 12px", borderRadius: 999,
                      background: toneColors.bg, color: toneColors.fg,
                      fontSize: 12, fontWeight: 600,
                    }}>
                    {meta.label}
                  </span>
                )}
              </div>
              {status.info_request_open && (
                <div className="mt-4 rounded-xl bg-[#FEF3C7] p-3 text-sm text-[#92400E] flex gap-2 items-start"
                      data-testid="status-info-request-notice">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Additional information requested.</strong> Please
                    check the email address you provided on your application
                    — a HarborWell reviewer has reached out with follow-up
                    questions.
                  </div>
                </div>
              )}
              <div className="mt-4 flex items-center justify-end">
                <button type="button" onClick={() => load(ref)}
                  data-testid="status-refresh-btn"
                  className="inline-flex items-center gap-1 text-xs text-[#1E3A8A] hover:underline">
                  <RefreshCw className="w-3.5 h-3.5" /> Refresh
                </button>
              </div>
            </div>

            <div className="bg-white border border-[#E5E5E0] rounded-2xl p-5">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">
                Progress
              </div>
              <div data-testid="status-timeline">
                {status.simplified_timeline.map((step, i) => (
                  <TimelineStep key={step.key} step={step}
                                  isLast={i === status.simplified_timeline.length - 1} />
                ))}
              </div>
            </div>

            <div className="text-xs text-slate-500 text-center">
              Have questions? Reply to any HarborWell email or contact your
              partner administrator.
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
